import { DialogActions, DialogContent, DialogTitle, Divider, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useToast } from '@retail/hooks';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentTagChanges } from '@retail/my-assortment/context';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import {
  dateRangeSchema,
  DateRangeSchema,
  EditAssortmentTagsTable,
  SharedTagDates,
} from '@retail/products/components';
import { useProductsTFunction } from '@retail/products/i18n';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, Dialog, OutlinedButton } from '@shared/components';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

interface Props {
  open: boolean;
  selectedItems: AssortmentItemMinimal[];
  assortmentId: number;
  isSaving?: boolean;
  updateAssortmentTags: (changes: AssortmentTagChanges) => Promise<void>;
  resetCheckedItems: () => void;
  onClose: () => void;
}

function EditAssortmentTagsDialog({
  open,
  selectedItems,
  assortmentId,
  isSaving,
  updateAssortmentTags,
  resetCheckedItems,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const productsT = useProductsTFunction();
  const toast = useToast();
  const apiRef = useGridApiRef();
  const { isBrandContext } = useSelectedOrgUnit();

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = useMemo(
    () => assortmentTagDtos.map(AssortmentTag.fromDTO),
    [assortmentTagDtos]
  );

  const defaultDates: DateRangeSchema = useMemo(() => ({ fromDate: new Date(), toDate: null }), []);

  const { control, handleSubmit } = useForm<DateRangeSchema>({
    resolver: yupResolver(dateRangeSchema(productsT)),
    mode: 'onChange',
    defaultValues: defaultDates,
  });

  const [fromDate, toDate] = useWatch({ control, name: ['fromDate', 'toDate'] });

  const onSubmit = useCallback(
    ({ fromDate: fromDateValue, toDate: toDateValue }: DateRangeSchema) => {
      const updatedItems = apiRef.current
        ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
        : [];

      const validFromDate = isBrandContext ? fromDateValue : new Date();
      const validToDate = isBrandContext ? toDateValue : null;

      const validFrom = getISO_8601Date(validFromDate);
      const validTo = validToDate ? getISO_8601Date(validToDate) : undefined;

      // Only the tags the user actually touched are posted: a tag is sent with the items it
      // was newly put on, and deleted item by item where it was taken off. Tags that were
      // already on the item are left alone unless the shared dates were changed, so a save
      // that only removes tags never reaches the create/update endpoint.
      const sharedDatesChanged =
        isBrandContext &&
        (getISO_8601Date(fromDateValue) !== getISO_8601Date(defaultDates.fromDate) ||
          !!toDateValue !== !!defaultDates.toDate);

      const itemIdsByTagId = new Map<number, number[]>();
      const removedItemIdsByTagId = new Map<number, number[]>();

      const persistedTagIdsByItemId = new Map(
        selectedItems.map((item) => [
          item.mgSupplierItemId,
          new Set(getActiveAssortmentTags(item.assortmentTags).map((tag) => tag.tagId)),
        ])
      );

      updatedItems.forEach((item) => {
        const persistedTagIds = persistedTagIdsByItemId.get(item.mgSupplierItemId);
        const activeTagIds = new Set(
          getActiveAssortmentTags(item.assortmentTags).map((tag) => tag.tagId)
        );

        activeTagIds.forEach((tagId) => {
          if (!persistedTagIds?.has(tagId) || sharedDatesChanged) {
            itemIdsByTagId.set(tagId, [
              ...(itemIdsByTagId.get(tagId) ?? []),
              item.mgSupplierItemId,
            ]);
          }
        });

        persistedTagIds?.forEach((tagId) => {
          if (!activeTagIds.has(tagId)) {
            removedItemIdsByTagId.set(tagId, [
              ...(removedItemIdsByTagId.get(tagId) ?? []),
              item.mgSupplierItemId,
            ]);
          }
        });
      });

      updateAssortmentTags({
        updates: Array.from(itemIdsByTagId, ([tagId, supplierItemIds]) => ({
          tagId,
          supplierItemIds,
          validFrom,
          validTo,
        })),
        removals: Array.from(removedItemIdsByTagId, ([tagId, supplierItemIds]) => ({
          tagId,
          supplierItemIds,
        })),
      })
        .then(() => {
          resetCheckedItems();
          onClose();
        })
        .catch((e) => {
          console.error(e);
          toast.error(t('myAssortment.actions.genericActionError'));
        });
    },
    [
      apiRef,
      defaultDates,
      isBrandContext,
      selectedItems,
      updateAssortmentTags,
      resetCheckedItems,
      onClose,
      toast,
      t,
    ]
  );

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>{t('myAssortment.actions.editTags')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexGrow: 1, height: '70vh' }} className="padding-0">
        <EditAssortmentTagsTable
          apiRef={apiRef}
          assortmentItems={selectedItems}
          assortmentTags={assortmentTags}
          validFrom={isBrandContext ? fromDate : undefined}
          validTo={isBrandContext ? toDate ?? undefined : undefined}
        />
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{ justifyContent: isBrandContext ? 'space-between' : 'flex-end', px: 4, py: 3 }}
      >
        {isBrandContext && <SharedTagDates control={control} />}
        <Stack flexDirection="row" gap={1}>
          <OutlinedButton size="small" onClick={onClose}>
            {t('myAssortment.cancel')}
          </OutlinedButton>
          <ContainedButton size="small" loading={isSaving} onClick={handleSubmit(onSubmit)}>
            {t('myAssortment.save')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(EditAssortmentTagsDialog);
